import { EmptyState, Table, type TableColumns } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { niceDate } from '../../utils/dateCalculations';
import { BpLink } from '../BpLink/BpLink';

const WorkMaterialsListFieldsDocument = graphql(`
  fragment WorkMaterialsListFields on WorkMaterial {
    uuid
    __typename
    title
    updated
  }
`);

type WorkMaterialsListProps = {
  workMaterial: FragmentType<typeof WorkMaterialsListFieldsDocument>[] | undefined;
  onNavigate?: (uuid: string) => void;
  isGroupEditor: boolean;
};

export type WorkMaterialTableType = {
  uuid: string;
  updated: string;
  title: string;
};

const WorkMaterialsList = ({ onNavigate, isGroupEditor, workMaterial }: WorkMaterialsListProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const memoizedData = useMemo(() => {
    return workMaterial?.map((wm) => {
      const singleWorkMaterial = getFragmentData(WorkMaterialsListFieldsDocument, wm);
      return {
        uuid: singleWorkMaterial.uuid,
        title: singleWorkMaterial.title,
        updated: niceDate(singleWorkMaterial.updated, 'medium', 'short') ?? '',
      };
    });
  }, [workMaterial]);

  function createColumns(): TableColumns<WorkMaterialTableType>[] {
    return [
      {
        id: 'title',
        size: 450,
        canExpand: true,
        header: t('common.name'),
        accessorKey: 'title',
        cell: ({ row }) => {
          return (
            <BpLink
              value={row.original.title}
              onNavigate={() => onNavigate?.(row.original.uuid)}
              maxWidth='740px'
              isEditorOfCourse={isGroupEditor}
            />
          );
        },
      },
      {
        id: 'updated',
        accessorKey: 'updated',
        header: t('common.updatedAt'),
      },
    ];
  }

  return !memoizedData || memoizedData.length === 0 ? (
    <EmptyState
      subtitle={t('workmaterials.noWorkmaterials')}
      fitParent
      padding='xl'
      size='small'
      forcedHeight='220px'
    />
  ) : (
    <Table<WorkMaterialTableType>
      columns={createColumns()}
      data={memoizedData}
      hideHeader={memoizedData.length === 0}
      customPadding='var(--spacing-6)'
      isDesktop={isDesktop}
      customRowHeight='var(--list-row-height)'
    />
  );
};

export { WorkMaterialsList };
